import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/kit";

// Square Web Payments SDK card field. Card data never touches our server — only the
// single-use token is sent to onToken(token, orderRef).
export default function SquarePayment({ amountCents, onToken, busy }) {
  const cardRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let script, card, cancelled = false;
    (async () => {
      try {
        const cfg = (await api.get("/square/public-config")).data;
        if (!cfg.enabled) { setErr("Square isn't connected yet."); return; }
        const src = cfg.environment === "production"
          ? "https://web.squarecdn.com/v1/square.js"
          : "https://sandbox.web.squarecdn.com/v1/square.js";
        await new Promise((res, rej) => {
          if (window.Square) return res();
          script = document.createElement("script");
          script.src = src; script.onload = res; script.onerror = rej;
          document.head.appendChild(script);
        });
        if (cancelled) return;
        const payments = window.Square.payments(cfg.application_id, cfg.location_id);
        card = await payments.card();
        await card.attach("#sq-card");
        cardRef.current = card;
        setReady(true);
      } catch (e) { setErr("Could not load the card form."); }
    })();
    return () => { cancelled = true; try { cardRef.current?.destroy?.(); } catch { /* noop */ } };
  }, []);

  const pay = async () => {
    setErr("");
    try {
      const result = await cardRef.current.tokenize();
      if (result.status !== "OK") { setErr(result.errors?.[0]?.message || "Card was not accepted."); return; }
      // One order_ref per attempt = idempotency key; reused if the user double-clicks.
      const orderRef = crypto.randomUUID();
      onToken(result.token, orderRef);
    } catch (e) { setErr("Payment could not be submitted."); }
  };

  return (
    <div data-testid="square-payment">
      <div id="sq-card" className="min-h-[90px] mb-3" />
      {err && <p className="text-[13px] text-maroon mb-2" data-testid="square-error">{err}</p>}
      <Button data-testid="square-pay-btn" onClick={pay} disabled={!ready || busy}>
        {busy ? "Processing…" : `Pay $${(amountCents / 100).toFixed(2)}`}
      </Button>
    </div>
  );
}
